import { getLocaleFirstDayOfWeek } from '@angular/common';
import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { AppConfig } from '@core/models/app-config';
import { AppLocale } from '@core/models/app-locale';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { LocalStorageService } from './local-storage.service';
import { Logger } from './logger.service';

@Injectable({
	providedIn: 'root'
})
export class AppLocaleService {

	private readonly COMPONENT_NAME = "AppLocaleService";

	private locales?: AppLocale[];
	currentLocale$ = new BehaviorSubject<AppLocale | undefined>(undefined);
	private currentLocale: AppLocale = new AppLocale();

	private readonly LocalStorageLocaleKey = "UserSelectedLocaleId";

	constructor (
		private logger: Logger,
		private _appConfigService: AppConfigService,
		private translate: TranslateService,
		private dateAdapter: DateAdapter<any>,
		private _localStorage: LocalStorageService
	) {
	}

	init(): Observable<AppLocale | undefined> {
		return this._appConfigService.config$.pipe(
			map((config: AppConfig | undefined) => {
				if (config) {
					this.locales = config?.locales;
					const locale = this.matchLocale(this.locales, config.defaultLocaleId);
					if (locale) {
						this.activateLocale(locale);
					}
					return locale;
				}
				return undefined;
			})
		);
	}

	userSelectLocale(localeId: string): void {
		// save the user selected locale id in the browser local storage
		this._localStorage.saveValue(this.LocalStorageLocaleKey, localeId);

		//
		this.activateLocaleById(localeId);
	}

	private matchLocale(locales: AppLocale[], defaultLocaleId: string): AppLocale | undefined {
		let selectedLocale: AppLocale | undefined = undefined;
		const savedLocaleId = this._localStorage.loadValue(this.LocalStorageLocaleKey);

		if (savedLocaleId) {
			const savedLocaleIdUppercase = savedLocaleId.toUpperCase();
			selectedLocale = locales.find((l: AppLocale) => l.localeId && l.localeId.toUpperCase() === savedLocaleIdUppercase);
		}

		if (!selectedLocale) {
			// read the browser's preferences and match the locale directly, and if not found, by language id only

			const browserLanguages = navigator.languages;
			if (browserLanguages && browserLanguages.length > 0) {

				const upperCaseBrowserLanguagesIds = browserLanguages.map((langId: string) => langId.toUpperCase());

				let upperCaseBrowserLanguageId = upperCaseBrowserLanguagesIds.find((uppercaseBrowserLanguageId: string) => {
					return locales.find((appLocale: AppLocale) => {
						if (appLocale.localeId && appLocale.localeId.toUpperCase() === uppercaseBrowserLanguageId) {
							selectedLocale = appLocale;
							return true;
						}
						return false;
					});
				});

				if (!upperCaseBrowserLanguageId) {
					// try to match the languages without the country suffix

					upperCaseBrowserLanguageId = upperCaseBrowserLanguagesIds.find((uppercaseBrowserLanguageId: string) => {
						const browserLanguageIdParts = uppercaseBrowserLanguageId.split("-");
						const baseBrowserLanguageId = browserLanguageIdParts[0].trim();
						return locales.find((appLocale: AppLocale) => {
							if (appLocale.localeId) {
								const appLocaleIdParts = appLocale.localeId.split("-");
								const baseAppLocaleIdParts = appLocaleIdParts[0].trim().toUpperCase();
								if (baseAppLocaleIdParts === baseBrowserLanguageId) {
									selectedLocale = appLocale;
									return true;
								}
							}
							return false;
						});
					});
				}
			}
		}

		if (!selectedLocale) {
			// pick up the default locale
			const upperCaseDefaultLocaleId = defaultLocaleId.toUpperCase();
			selectedLocale = locales.find((l: AppLocale) => l.localeId && l.localeId.toUpperCase() === upperCaseDefaultLocaleId);
		}

		return selectedLocale;
	}

	activateLocaleById(localeId: string | undefined): void {
		this.currentLocale = new AppLocale(); // reset it
		if (localeId) {
			this.locales?.some(elem => {
				if (elem.localeId === localeId) {
					this.activateLocale(elem);
					return true; // break the loop
				}
				return false;
			});
		}
	}

	private activateLocale(locale: AppLocale): void {
		this.currentLocale = locale;
		this.logger.info(this.COMPONENT_NAME, `Using locale ID: "${this.currentLocale.localeId}"`);
		if (this.currentLocale.uiLanguageFile) {
			this.logger.info(this.COMPONENT_NAME, `Using language file: "${this.currentLocale.uiLanguageFile}"`);
			this.translate.use(this.currentLocale.uiLanguageFile);
		}
		this.initDateAdapter();
		this.currentLocale$.next(this.currentLocale);
	}

	private initDateAdapter(): void {
		this.dateAdapter.setLocale(this.currentOrDefaultLocaleId);

		// NativeDateAdapter.getFirstDayOfWeek() returns hard-coded 0, so we need to shim the method so that mat controls can call it.
		// Furthemore, angular's getLocaleFirstDayOfWeek() uses "official" first day of week but this varies to local custom.
		// E.g. In Australia, officially it is Sunday (and in angular), but people use Monday (and is the preferred first day of week).
		// So, allow sites to override in app-config, and otherwise default to the below hard-coded list, otherwise default to angular's getLocaleFirstDayOfWeek().
		if (typeof this.currentLocale.firstDayOfWeek === "undefined") {
			switch (this.currentOrDefaultLocaleId) {
				case "en-AU":
					this.currentLocale.firstDayOfWeek = 1;
					break;

				default:
					this.currentLocale.firstDayOfWeek = getLocaleFirstDayOfWeek(this.currentOrDefaultLocaleId);
			}
		}
		// Shim the DateAdapter
		this.dateAdapter.getFirstDayOfWeek = () => this.currentLocale.firstDayOfWeek ?? 0;
	}

	get currentOrDefaultLocaleId(): string {
		return this.currentLocale.localeId ?? "en-AU";
	}

	get timestampFormat(): string {
		return this.currentLocale.timestampFormat ?? "E d MMM h:mma"
	}

	get dateFormat(): string {
		return this.currentLocale.dateFormat ?? "E d MMM y"
	}
}
